import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, Loader2, CheckCircle } from 'lucide-react';
import PrivacyConsent from '../forms/PrivacyConsent';
import { facebookPixelEvents } from '../../utils/facebookPixel';
import sendGridService from '../../lib/api/sendGridService';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  homeschoolType: string;
  studentAge: string;
  homeschoolRole: string;
}

interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

const sendGridListId = import.meta.env.VITE_SENDGRID_LIST_HS_SUMMER25_ID;

// Debug logging for list ID
console.log('SendGrid List ID:', sendGridListId);
if (!sendGridListId) {
  console.error('VITE_SENDGRID_LIST_HS_SUMMER25_ID environment variable is not set!');
}

export function FacebookStyleForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    homeschoolType: '',
    studentAge: '',
    homeschoolRole: ''
  });
  const [utmParams, setUtmParams] = useState<UTMParams>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Capture UTM parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const utms: UTMParams = {};
    
    // Extract all UTM parameters
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        utms[param as keyof UTMParams] = value;
      }
    });
    
    setUtmParams(utms);
    
    // Store UTM params in sessionStorage for persistence across page reloads
    if (Object.keys(utms).length > 0) {
      sessionStorage.setItem('utmParams', JSON.stringify(utms));
    } else {
      // Try to get from sessionStorage if not in URL (direct navigation)
      const storedUtms = sessionStorage.getItem('utmParams');
      if (storedUtms) {
        setUtmParams(JSON.parse(storedUtms));
      }
    }
  }, []);

  const getRedirectUrl = () => {
    // Customize redirect based on UTM parameters
    // const { utm_source, utm_campaign, utm_content } = utmParams;
    
    return 'https://www.homeschool.emploskills.com/vbc-online';
    // Example: Different redirects based on campaign
    // if (utm_campaign?.includes('summer2025')) {
    //   return 'https://platform.emploskills.com/summer-2025-signup';
    // }
    
    // if (utm_source === 'facebook' && utm_content?.includes('trial')) {
    //   return 'https://platform.emploskills.com/free-trial';
    // }
    
    // Default redirect
    return '/';
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1 && formData.homeschoolType && formData.studentAge) {
      setStep(2);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      handleNext(e);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Check if we have a list ID
      if (!sendGridListId) {
        throw new Error('SendGrid List ID is not configured. Please set VITE_SENDGRID_LIST_HS_SUMMER25_ID environment variable.');
      }

      // Track form submission with Facebook Pixel
      facebookPixelEvents.lead({
        content_name: 'Facebook Landing Form Submission',
        content_category: 'facebook_landing',
        content_type: 'form_submission'
      });

      // Submit to SendGrid via backend service
      console.log('Submitting to SendGrid with List ID:', sendGridListId);
      const sendGridSuccess = await sendGridService.addFacebookFormContact(
        formData,
        utmParams,
        sendGridListId
      );

      if (sendGridSuccess) {
        // Track successful form submission
        facebookPixelEvents.lead({
          content_name: 'Facebook Landing Form Success',
          content_category: 'facebook_landing',
          content_type: 'form_success'
        });

        facebookPixelEvents.completeRegistration({
          content_name: 'Facebook Landing Registration Complete',
          content_category: 'facebook_landing',
          content_type: 'registration_success'
        });
        
        // Show success message
        setShowSuccess(true);
        
        // Redirect based on UTM parameters after 10 seconds
        setTimeout(() => {
          const redirectUrl = getRedirectUrl();
          if (redirectUrl.startsWith('http')) {
            window.location.href = redirectUrl;
          } else {
            navigate(redirectUrl);
          }
        }, 10000);
      } else {
        console.error('SendGrid submission failed');
        throw new Error('SendGrid submission failed');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm border border-[#dddfe2]">
      <div className="bg-[#f0f2f5] px-4 py-6 border-b border-[#dddfe2] text-center">
        <div className="relative w-24 h-24 mx-auto mb-4">
          <img 
            src="/homeschoollogo.jpg" 
            alt="EmploSkills Homeschool" 
            className="w-full h-full rounded-full object-cover border-4 border-white shadow-lg"
          />

        </div>
        <h1 className="text-[#1c1e21] text-2xl font-bold mb-2">
          EmploSkills Homeschool
        </h1>
        <h2 className="text-[#1c1e21] text-lg font-semibold mb-2">
          Sign up to show your interest and learn more.
        </h2>
        <p className="text-sm text-gray-600 max-w-sm mx-auto">
          Fill out this form and we will send you more information.
        </p>
      </div>
      
      {showSuccess ? (
        <div className="p-6 text-center">
          <div className="mb-4 text-green-600">
            <CheckCircle className="w-16 h-16 mx-auto" />
          </div>
          <h2 className="text-xl font-semibold mb-4">Thank You for Your Interest!</h2>
          <p className="text-gray-600 mb-2">
            You will receive an email shortly with more information.
          </p>
          <p className="text-gray-500 text-sm">
            If you don't receive an email, please check your spam folder.
          </p>
          <p className="text-gray-400 text-sm mt-4">
            Redirecting you in a few seconds...
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {step === 1 ? (
            <>
              <div>
                <div className="flex items-center gap-1">
                  <label htmlFor="homeschoolRole" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Role <span className="text-red-500">*</span>
                  </label>
                  <div className="relative group">
                    <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                    <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-sm rounded-md shadow-lg z-50">
                      <div className="relative">
                        Please select your role in homeschooling
                        <div className="absolute top-1/2 -translate-y-1/2 -left-1 w-2 h-2 bg-gray-900 rotate-45"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <select
                  id="homeschoolRole"
                  name="homeschoolRole"
                  value={formData.homeschoolRole}
                  onChange={handleChange}
                  required
                  className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm p-2"
                >
                  <option value="">Select</option>
                  <option value="homeschool_parent">Homeschool Parent</option>
                  <option value="homeschool_teacher">Homeschool Educator</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <div className="flex items-center gap-1">
                  <label htmlFor="homeschoolType" className="block text-sm font-medium text-gray-700 mb-1">
                    Type of Homeschool <span className="text-red-500">*</span>
                  </label>
                  <div className="relative group">
                    <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                    <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-sm rounded-md shadow-lg z-50">
                      <div className="relative">
                        Please specify what type of HomeSchooling arrangement you have
                        <div className="absolute top-1/2 -translate-y-1/2 -left-1 w-2 h-2 bg-gray-900 rotate-45"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <select
                  id="homeschoolType"
                  name="homeschoolType"
                  value={formData.homeschoolType}
                  onChange={handleChange}
                  required
                  className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm p-2"
                >
                  <option value="">Select</option>
                  <option value="individual_household">Individual Household</option>
                  <option value="homeschool_coop">Homeschool Cooperative</option>
                  <option value="micro_schools">Micro School</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <div className="flex items-center gap-1">
                  <label htmlFor="studentAge" className="block text-sm font-medium text-gray-700 mb-1">
                    Student Age <span className="text-red-500">*</span>
                  </label>
                </div>
                <select
                  id="studentAge"
                  name="studentAge"
                  value={formData.studentAge}
                  onChange={handleChange}
                  required
                  className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm p-2"
                >
                  <option value="">Select</option>
                  <option value="middle_school">Middle School</option>
                  <option value="high_school">High School</option>
                  <option value="m_h_school">Middle & High School</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <button
                type="submit"
                className="w-full bg-[#1877f2] hover:bg-[#166fe5] text-white font-semibold py-2 px-4 rounded-md transition-colors"
              >
                Next
              </button>
            </>
          ) : (
            <>
              <div className="flex gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First name"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 bg-[#f5f6f7] border border-[#dddfe2] rounded-md text-[15px] focus:border-[#1877f2] focus:outline-none"
                  />
                </div>
                <div className="flex-1">
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Last name"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 bg-[#f5f6f7] border border-[#dddfe2] rounded-md text-[15px] focus:border-[#1877f2] focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 bg-[#f5f6f7] border border-[#dddfe2] rounded-md text-[15px] focus:border-[#1877f2] focus:outline-none"
                />
              </div>

              <div>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 bg-[#f5f6f7] border border-[#dddfe2] rounded-md text-[15px] focus:border-[#1877f2] focus:outline-none"
                />
              </div>

              {error && (
                <div className="text-[#be4b49] text-sm bg-[#ffebe8] p-2 rounded border border-[#dd3c37]">
                  {error}
                </div>
              )}

              <div className="mt-6">
                <PrivacyConsent 
                  onConsentChange={(field, value) => console.log(field, value)}
                  required={true}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#1877f2] hover:bg-[#166fe5] text-white font-semibold py-2 px-4 rounded-md transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Submitting...
                  </span>
                ) : (
                  'Submit'
                )}
              </button>

              <p className="text-xs text-[#65676b] text-center mt-4">
                By submitting this form, you agree to our{' '}
                <a href="/privacy-policy" className="text-[#1877f2] hover:underline">
                  Privacy Policy
                </a>
              </p>
            </>
          )}
        </form>
      )}
    </div>
  );
} 